/**
 * Password validation utilities and requirements
 */

export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  hasUppercase: /[A-Z]/,
  hasLowercase: /[a-z]/,
  hasNumber: /\d/,
  hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
} as const

export interface PasswordRequirement {
  id: string
  label: string
  validator: (password: string) => boolean
}

export const PASSWORD_REQUIREMENT_LIST: PasswordRequirement[] = [
  {
    id: 'minLength',
    label: `At least ${PASSWORD_REQUIREMENTS.minLength} characters`,
    validator: (password) => password.length >= PASSWORD_REQUIREMENTS.minLength,
  },
  {
    id: 'hasUppercase',
    label: 'At least one uppercase letter (A-Z)',
    validator: (password) => PASSWORD_REQUIREMENTS.hasUppercase.test(password),
  },
  {
    id: 'hasLowercase',
    label: 'At least one lowercase letter (a-z)',
    validator: (password) => PASSWORD_REQUIREMENTS.hasLowercase.test(password),
  },
  {
    id: 'hasNumber',
    label: 'At least one number (0-9)',
    validator: (password) => PASSWORD_REQUIREMENTS.hasNumber.test(password),
  },
  {
    id: 'hasSpecialChar',
    label: 'At least one special character (!@#$%^&* etc.)',
    validator: (password) => PASSWORD_REQUIREMENTS.hasSpecialChar.test(password),
  },
]

/**
 * Check if a password meets all requirements
 */
export function isPasswordValid(password: string): boolean {
  return PASSWORD_REQUIREMENT_LIST.every((req) => req.validator(password))
}

/**
 * Get the met requirements for a password
 */
export function getMetRequirements(password: string): string[] {
  return PASSWORD_REQUIREMENT_LIST.filter((req) =>
    req.validator(password)
  ).map((req) => req.id)
}

/**
 * Calculate password strength as a percentage (0-100)
 */
export function calculatePasswordStrength(password: string): number {
  const metCount = getMetRequirements(password).length
  const totalRequirements = PASSWORD_REQUIREMENT_LIST.length
  return Math.round((metCount / totalRequirements) * 100)
}
